import React from 'react'
import { cn } from '@amusnet/react-ui'
import { useTranslation } from '@amusnet/translations/react'
import { ViewportType } from 'game-ui-types'
import { ULTRA_PLAY_SPIN_OPTIONS } from '@stores'
import { formatCurrency } from '@amusnet/amusnet-common'
import UltraPlayTable from './UltraPlayTable'
import UltraPlayModalHeader from './UltraPlayModalHeader'
import UltraPlayStatsRow from './UltraPlayStatsRow'
import UltraPlaySliderRow from './UltraPlaySliderRow'
import UltraPlayFeatureSettings from './UltraPlayFeatureSettings'
import UltraPlayPlayPauseButton from './UltraPlayPlayPauseButton'
import UltraPlayPortraitFooter from './UltraPlayPortraitFooter'
import { useUltraPlayControls } from './useUltraPlayControls'

interface UltraPlayControlsProps {
    onClose: () => void
}

const UltraPlayControls: React.FC<UltraPlayControlsProps> = ({ onClose }) => {
    const { t } = useTranslation()
    const controls = useUltraPlayControls()
    const {
        status,
        totalBet,
        totalWin,
        currency,
        hideCents,
        gameName,
        viewport,
        isSetup,
        isPlaying,
        isLandscape,
        isPortrait,
        currentBetAmount,
        spinsValue,
        selectedBetIndex,
        selectedSpinsIndex,
        betOptions,
        stopOnFeature,
        setStopOnFeature,
        isNoConnection,
        controlsDisabled,
        playPauseDisabled,
        isPausePending,
        selectedBuyBonusFeatureName,
        buyBonusFeatures,
        hasBuyBonus,
        hasDoubleChance,
        canDecrementSpins,
        canIncrementSpins,
        handleBetIndexChange,
        handleBetDecrement,
        handleBetIncrement,
        handleSpinsIndexChange,
        handleSpinsCommit,
        handleSpinsDecrement,
        handleSpinsIncrement,
        handleBuyBonusChange,
        handlePlayPause,
    } = controls

    // No connection overrides the badge (red "NO CONNECTION") and disables play everywhere.
    const statusLabel = isNoConnection ? 'NO_CONNECTION' : status

    // ── Controls panel — same JSX for portrait setup and landscape/desktop ────

    const controlsPanel = (
        <>
            <div className={cn('flex flex-col flex-1 overflow-y-auto min-h-0 px-4 py-3')}>
                {isPortrait && (
                    <p className="text-[32px] font-normal leading-[36px] text-white mb-[40px] whitespace-pre-line">
                        {t('gameui.ultraplay.description')}
                    </p>
                )}

                <UltraPlaySliderRow
                    isPortrait={isPortrait}
                    isLandscape={isLandscape}
                    label={t('gameui.bet')}
                    amount={formatCurrency(currentBetAmount, { hideCents })}
                    currency={currency}
                    currentIndex={selectedBetIndex}
                    maxIndex={betOptions.length - 1}
                    onDecrement={handleBetDecrement}
                    onIncrement={handleBetIncrement}
                    onIndexChange={handleBetIndexChange}
                    canDecrement={selectedBetIndex > 0}
                    canIncrement={selectedBetIndex < betOptions.length - 1}
                    disabled={controlsDisabled}
                />

                <UltraPlaySliderRow
                    isPortrait={isPortrait}
                    isLandscape={isLandscape}
                    label={t('common.spinsUppercase')}
                    amount={spinsValue}
                    currentIndex={selectedSpinsIndex}
                    maxIndex={ULTRA_PLAY_SPIN_OPTIONS.length - 1}
                    onDecrement={handleSpinsDecrement}
                    onIncrement={handleSpinsIncrement}
                    onIndexChange={handleSpinsIndexChange}
                    onCommit={handleSpinsCommit}
                    canDecrement={canDecrementSpins}
                    canIncrement={canIncrementSpins}
                    disabled={controlsDisabled}
                />

                <UltraPlayFeatureSettings
                    variant="panel"
                    isPortrait={isPortrait}
                    isLandscape={isLandscape}
                    stopOnFeature={stopOnFeature}
                    setStopOnFeature={setStopOnFeature}
                    hasBuyBonus={hasBuyBonus}
                    hasDoubleChance={hasDoubleChance}
                    buyBonusFeatures={buyBonusFeatures}
                    buyBonusValue={selectedBuyBonusFeatureName}
                    onBuyBonusChange={handleBuyBonusChange}
                    disabled={controlsDisabled}
                />
            </div>

            <div
                className={cn(
                    'flex justify-center ',
                    isPortrait && 'mt-auto py-[30px] bg-black',
                    isLandscape && 'py-[15px]',
                    !isPortrait && !isLandscape && 'px-[55px]'
                )}
            >
                <UltraPlayPlayPauseButton
                    isPlaying={isPlaying}
                    onClick={handlePlayPause}
                    size={isLandscape ? 120 : 180}
                    disabled={!betOptions.length || playPauseDisabled}
                    className={cn('disabled:opacity-40', isPausePending && 'opacity-40')}
                    pulse={status === 'READY'}
                />
            </div>
        </>
    )

    // ── PORTRAIT ──────────────────────────────────────────────────────────────

    if (isPortrait) {
        return (
            <div className="w-full h-full flex flex-col">
                <UltraPlayModalHeader gameName={gameName} isLandscape={isLandscape} onClose={onClose} />

                <UltraPlayStatsRow
                    totalBet={totalBet}
                    totalWin={totalWin}
                    status={statusLabel}
                    currency={currency}
                    hideCents={hideCents}
                    isPortrait={isPortrait}
                />

                {/* Table — flex-shrink-0 in setup so controlsPanel can take flex-1 */}
                <div
                    className={cn('flex flex-col overflow-hidden px-2.5', isSetup ? 'flex-shrink-0' : 'flex-1 min-h-0')}
                >
                    <UltraPlayTable isSetup={isSetup} />
                </div>

                {/* Setup: shared controls panel. Active/history: portrait-only footer */}
                {isSetup ? controlsPanel : <UltraPlayPortraitFooter controls={controls} />}
            </div>
        )
    }

    // ── LANDSCAPE / DESKTOP ───────────────────────────────────────────────────

    const rightColWidthClass = viewport === ViewportType.DESKTOP ? 'w-[620px]' : 'w-[450px]'

    return (
        <div className="w-full h-full flex flex-row">
            {/* Left column: stats + table */}
            <div className="flex-1 flex flex-col overflow-hidden border-r border-white/10 min-w-0">
                <UltraPlayStatsRow
                    totalBet={totalBet}
                    totalWin={totalWin}
                    status={statusLabel}
                    currency={currency}
                    hideCents={hideCents}
                    isLandscape={isLandscape}
                />
                <div className="flex-1 min-h-0 overflow-hidden">
                    <UltraPlayTable isSetup={isSetup} />
                    {isSetup && (
                        <p className="py-2.5 px-[30px] text-[32px] font-normal leading-[36px] text-white whitespace-pre-line">
                            {t('gameui.ultraplay.description')}
                        </p>
                    )}
                </div>
            </div>

            {/* Right column: logo + controls panel */}
            <div className={cn('flex-shrink-0 flex flex-col bg-black overflow-hidden', rightColWidthClass)}>
                <UltraPlayModalHeader gameName={gameName} isLandscape={isLandscape} onClose={onClose} />

                <div className="flex-1 overflow-y-auto">{controlsPanel}</div>
            </div>
        </div>
    )
}

export default UltraPlayControls
